/**
 * y-grid — Yjs ↔ @fredericrous/grid binding.
 *
 * Syncs one grid's items (identified by `gridId`) between a Yjs doc
 * and the global `useGridStore`. Designed to sit beside `y-xyflow`:
 * the xyflow canvas binds the sitemap structure; y-grid binds the
 * content layout inside each page node.
 *
 * ## Yjs structure
 *
 * One Y.Map per grid, keyed by `"grid:<gridId>"` in the shared doc.
 * Each entry: `itemId → Y.Map` holding the item's fields (id, position,
 * constraints, contentType, contentProps, nestedGridId).
 *
 * Items are NESTED Y.Maps (not whole-object plain values) so a field update —
 * an async auto-grow re-measuring an item's height — sets ONE field INSIDE the
 * item map rather than replacing the whole top-level entry. That is what makes a
 * CROSS-GRID move undoable without a duplicate: the move inserts the item's map
 * into the target grid (a tracked, undoable top-level `set`); a CONCURRENT
 * context that re-measures the moved item writes a FIELD inside that map
 * (untracked, via BroadcastChannel). Undoing the move deletes the whole target
 * key — the nested map and any concurrent field write go with it — so the item
 * can't survive in both grids. Were items whole-object values, that concurrent
 * re-measure would `set` a NEWER top-level struct at the same key which the
 * undo of the move can't reach, leaving the item duplicated in both grids.
 * (Config `::config` and the `::bp:` override cells stay plain whole-object
 * values — they never move between grids.) Legacy docs with plain-object items
 * are read transparently and upgraded to a nested map on the next write.
 *
 * ## Sync directions (grid 0.9.3 sync seams)
 *
 * **Direction 1 (store → Yjs):** `subscribeGridsCommitted(useGridStore, …)` —
 * ONE event per committed store mutation, naming every touched grid. A per-doc
 * coordinator (see {@link docSyncOf}) flushes all of the doc's bound grids the
 * event names inside ONE `transact(LOCAL_ORIGIN)`, so a cross-grid drop (one
 * event naming both grids) commits — and undoes — atomically. Events tagged
 * `"remote"` are our own inbound echo and are skipped; `"autogrow"` events and
 * anything landing mid-gesture are left to `subscribeSettled`'s quiesce pass,
 * which reconciles every bound grid once the gesture + trailing auto-grow
 * writes go quiet. Reconciles diff against what the Y.Map ALREADY holds, so a
 * net-zero pass writes nothing (no phantom undo steps).
 *
 * **Direction 2 (Yjs → store):** Y.Map `observe` fires on remote-origin
 * transactions. The binding reconstructs a `SerializedGridLayout` from the doc
 * and calls `applyRemoteLayout(gridId, layout, { read })`: an identity-
 * preserving diff (value-equal items keep object identity; nothing changed →
 * no store write at all) whose commit is tagged `"remote"` so direction 1
 * skips the echo. While a gesture is live the STORE defers with invalidate-
 * and-reread semantics — at gesture end (after the gesture's own commit has
 * been flushed to the doc) it calls our `read()`, which re-derives the layout
 * from the CURRENT resolved doc snapshot, so remote edits that merged
 * mid-gesture are never lost. `applyRemoteLayout` bypasses collision-detection
 * and compaction like `importLayout`, so the ADR-001–005 interaction behaviors
 * are NOT triggered by remote syncs. That is intentional: a remote peer's
 * layout is trusted as-is.
 *
 * ## Human-review notes
 *
 * 1. **Config sync is GAP-ONLY** — items (positions + content) plus the
 *    gap trio (`gap`/`gapX`/`gapY`, editable in-canvas since grid 0.8.32)
 *    are synced through Yjs; the gaps ride a reserved `"::config"` key in
 *    the same Y.Map. The REST of the config (columns, rowHeight,
 *    compactType) is set once at grid-creation time and treated as
 *    session-local. Full multi-peer config changes are out of scope.
 *
 * 2. **applyRemoteLayout makes the Y.Map the truth** — direction 2
 *    rebuilds the grid's items record from the doc (diff-applied). A
 *    remote update landing mid-gesture no longer disrupts the live
 *    interaction: the store defers it and re-reads at gesture end.
 *
 * 3. **ADR compliance** — the binding never calls `moveItem`,
 *    `resizeItem`, or any other action that triggers layout engine
 *    recalculation. Remote updates go through `applyRemoteLayout` only.
 */
import * as Y from "yjs";
// Use sub-path imports to avoid pulling in React components (which use
// StyleX and require a babel transform) and to sidestep the GridItem
// name collision (GridItem is both a React component and a data type
// in the root export).
// (0.9.3 anchors zustand's middleware augmentations in its emitted d.ts, so
// the type-only `zustand/middleware` imports 0.9.2 required are gone.)
import {
  useGridStore,
  selectIsInteracting,
  subscribeGridsCommitted,
  subscribeSettled,
  upgradeSerializedLayout,
} from "@fredericrous/grid/store";
import type { GridItem, SerializedGridLayout } from "@fredericrous/grid/types";

export const LOCAL_ORIGIN = Symbol.for("y-grid-local");

/**
 * Origin for BASELINE writes that must never be undoable: the one-time
 * legacy→flat item migration, and a grid's initial push WHILE the doc is
 * hydrating (see `setGridDocSeeding`). The flat item store is in the editor's
 * UndoManager scope from creation, so these would otherwise land as the bottom
 * undo step (a stray Cmd+Z could blank a fresh page or un-migrate storage). NOT
 * a tracked origin → never captured. It IS still forwarded to peers and
 * persisted (only `undo` skips it), and it's non-LOCAL so a bound grid's
 * `onRemote` re-seeds from it — a harmless idempotent re-import.
 */
const SEED_ORIGIN = Symbol.for("y-grid-seed");

/**
 * Docs currently in their INITIAL hydration window. A grid's originator push
 * (store→Yjs, `else` branch of initial sync) is a non-undoable seed while the
 * doc is hydrating, but an UNDOABLE user action afterwards — a mid-session
 * "add section/widget" must undo cleanly (remove the block AND its items), not
 * leave the pushed items orphaned in the flat store. The editor marks the doc
 * seeding at creation and unmarks it once loaded (see useEditorDoc).
 */
const seedingDocs = new WeakSet<Y.Doc>();

/** Mark/unmark a doc's initial-hydration window (see `seedingDocs`). While
 *  seeding, originator pushes are non-undoable; afterwards they're undoable. */
export function setGridDocSeeding(ydoc: Y.Doc, seeding: boolean): void {
  if (seeding) seedingDocs.add(ydoc);
  else seedingDocs.delete(ydoc);
}

// Every reserved (non-item) key in a grid's Y.Map carries the `::` prefix.
// Item ids are store-generated (`id_*`) or widget slugs (alphanumeric/hyphen),
// so the whole `::` namespace is safe to reserve — nothing collides.
const isReserved = (k: string): boolean => k.startsWith("::");

/** Reserved key for the synced config subset (gap trio). */
const CONFIG_KEY = "::config";

/**
 * SINGLE-PARENT item store. Every grid's items live in ONE flat doc-level Y.Map
 * `grid-items`, keyed by itemId, each value a nested Y.Map of the item's fields
 * PLUS a `::parent` field naming the grid it belongs to. A grid's membership is
 * DERIVED by filtering on `::parent` — it is NOT the map an item sits in.
 *
 * Why: a cross-grid move is then a SINGLE LWW write to `::parent`, not
 * delete-from-A + insert-into-B across two maps. Consequences:
 *   • An item has exactly ONE parent by construction → it can NEVER be in two
 *     grids at once. Duplication (incl. the undo-echo class) is impossible, not
 *     patched.
 *   • Concurrent moves of the SAME item resolve deterministically by LWW on the
 *     one `::parent` register instead of both landing.
 *   • Undo of a move reverts ONLY `::parent`, so a concurrent edit to the moved
 *     item's content (a different field) survives — no lost update on undo.
 * (Per-grid `grid:<id>` maps still hold `::config` + `::bp:` cells — those never
 * move between grids. Legacy docs store items inside the grid maps; the bind
 * migrates them into the flat store, tolerating both on read.)
 */
const ITEMS_MAP = "grid-items";
/** Field on an item's nested map naming its owning grid (the LWW parent). */
const PARENT_KEY = "::parent";

/** Name of the doc-level flat item store's Y.Map. Editors add it to the
 *  UndoManager scope (items are undoable) ALONGSIDE the per-grid `grid:<id>`
 *  maps, which still hold undoable config/`::bp:` cells. */
export const GRID_ITEMS_MAP = ITEMS_MAP;

/**
 * Per-breakpoint LAYOUT overrides (Phase 2 responsive model). Base
 * `item.position` is always the DESKTOP layout; a breakpoint override is a
 * cascade — an item with no override inherits the base. Content is shared across
 * breakpoints; only layout forks.
 *
 * ## Storage — one Y.Map entry PER (breakpoint, item)
 *
 * Each override is its own top-level key `::bp:<breakpoint>:<itemId>` →
 * `{ position?, hidden? }` (plain JSON). Keying per item (rather than one
 * whole-object `::breakpoints` blob) is what makes CONCURRENT mobile edits
 * merge: two peers pinning different items on mobile touch different keys, so
 * neither clobbers the other (the blob was last-write-wins over the whole map).
 * Only two peers editing the SAME item+breakpoint conflict — genuine contention,
 * LWW like a base item. Being top-level keys, they ride the existing shallow
 * `observe`/`importLayout` paths (no deep observation) and stay out-of-band from
 * the published GridItem shape. Pre-v2 docs used a single `::breakpoints` object;
 * `migrateLegacyBp` converts it on bind.
 */
const BP_PREFIX = "::bp:";
const LEGACY_BP_KEY = "::breakpoints";

export type Breakpoint = "desktop" | "tablet" | "mobile";
/** A breakpoint slug as stored in a `::bp:` key (the non-desktop tiers). */
type OverrideBreakpoint = "tablet" | "mobile";
type PositionsByItem = Record<string, GridItem["position"]>;
/** One (breakpoint, item) override cell. */
type BpEntry = { position?: GridItem["position"]; hidden?: true };
/** Per-breakpoint layer: sparse position overrides + a hidden-item set. */
type MobileLayer = {
  positions?: PositionsByItem;
  hidden?: Record<string, true>;
};
type BreakpointOverrides = Partial<Record<OverrideBreakpoint, MobileLayer>>;

const bpEntryKey = (bp: OverrideBreakpoint, itemId: string): string =>
  `${BP_PREFIX}${bp}:${itemId}`;

/** Parse a `::bp:<breakpoint>:<itemId>` key, or null if it isn't one. Splits on
 *  the FIRST colon after the prefix, so item ids containing `:` are safe. */
function parseBpKey(
  k: string,
): { bp: OverrideBreakpoint; itemId: string } | null {
  if (!k.startsWith(BP_PREFIX)) return null;
  const rest = k.slice(BP_PREFIX.length);
  const i = rest.indexOf(":");
  if (i < 0) return null;
  return {
    bp: rest.slice(0, i) as OverrideBreakpoint,
    itemId: rest.slice(i + 1),
  };
}

/** Assemble the normalized override layers from a grid Y.Map's `::bp:` entries.
 *  Tolerates a pre-v2 legacy `::breakpoints` blob (read-through before the bind
 *  migration lands, or in a doc that never bound). */
function readOverrides(yItems: Y.Map<unknown>): BreakpointOverrides {
  const out: BreakpointOverrides = {};
  let sawEntry = false;
  yItems.forEach((v, k) => {
    const parsed = parseBpKey(k);
    if (!parsed) return;
    sawEntry = true;
    const entry = v as BpEntry;
    const layer = (out[parsed.bp] ??= {});
    if (entry.position)
      (layer.positions ??= {})[parsed.itemId] = entry.position;
    if (entry.hidden) (layer.hidden ??= {})[parsed.itemId] = true;
  });
  if (!sawEntry) {
    const legacy = yItems.get(LEGACY_BP_KEY);
    if (legacy && typeof legacy === "object")
      return legacy as BreakpointOverrides;
  }
  return out;
}

// The device the canvas is authoring for — a module singleton, matching the
// module-singleton grid store: every bound grid resolves/routes against the
// SAME active breakpoint. The editor sets it from SitemapInteraction.breakpoint.
let activeBreakpoint: Breakpoint = "desktop";
// One re-seed callback per bound grid; a breakpoint switch re-resolves them all.
const reseeders = new Set<() => void>();

/** Switch the device the canvas authors for. Re-seeds every bound grid's store
 *  with the resolved positions for `bp` (overrides for mobile, base for
 *  desktop). Idempotent. */
export function setActiveBreakpoint(bp: Breakpoint): void {
  if (bp === activeBreakpoint) return;
  activeBreakpoint = bp;
  for (const reseed of reseeders) reseed();
}

export function getActiveBreakpoint(): Breakpoint {
  return activeBreakpoint;
}

// ── Editor-facing per-item mobile state (badge + hide) ──────────────
// The badge/hide chrome reads and mutates the override layer directly (it lives
// in Yjs, out-of-band from the grid store). Hiding does NOT move the item — the
// canvas just dims it and publish will drop it — so these never re-seed the
// store; the editor re-renders from `observeBreakpoints`.

/** The non-desktop tiers an item can be individually overridden / hidden at. */
export type EditableBreakpoint = OverrideBreakpoint;

/** Whether an item is position-overridden (hand-placed) and/or hidden at a given
 *  non-desktop breakpoint. */
export function readBreakpointFlags(
  ydoc: Y.Doc,
  gridId: string,
  itemId: string,
  bp: EditableBreakpoint,
): { overridden: boolean; hidden: boolean } {
  const yItems = ydoc.getMap<unknown>(`grid:${gridId}`);
  const entry = yItems.get(bpEntryKey(bp, itemId)) as BpEntry | undefined;
  if (entry) return { overridden: !!entry.position, hidden: !!entry.hidden };
  // Legacy blob fallback (pre-migration).
  const layer = readOverrides(yItems)[bp];
  return {
    overridden: !!layer?.positions?.[itemId],
    hidden: !!layer?.hidden?.[itemId],
  };
}

/** Hide or show an item at a non-desktop breakpoint. Merges into the per-item
 *  override cell so a position override on the same item is preserved; a cell
 *  with neither a position nor a hidden flag is removed. */
export function setBreakpointHidden(
  ydoc: Y.Doc,
  gridId: string,
  itemId: string,
  bp: EditableBreakpoint,
  hidden: boolean,
): void {
  const yItems = ydoc.getMap<unknown>(`grid:${gridId}`);
  const key = bpEntryKey(bp, itemId);
  ydoc.transact(() => {
    const cur = (yItems.get(key) as BpEntry | undefined) ?? {};
    const next: BpEntry = { ...cur };
    if (hidden) next.hidden = true;
    else delete next.hidden;
    if (next.position === undefined && !next.hidden) yItems.delete(key);
    else yItems.set(key, next);
  }, LOCAL_ORIGIN);
}

/** Clear an item's POSITION override at a tier ("reset to Desktop"): the item
 *  drops back to the tier default (tablet → the desktop layout; mobile → the
 *  auto-stack). Keeps a `hidden` flag (that's the eye toggle's concern); removes
 *  the cell if nothing's left. The store re-seeds from Yjs on the next observe. */
export function clearBreakpointPosition(
  ydoc: Y.Doc,
  gridId: string,
  itemId: string,
  bp: EditableBreakpoint,
): void {
  const yItems = ydoc.getMap<unknown>(`grid:${gridId}`);
  const key = bpEntryKey(bp, itemId);
  const cur = yItems.get(key) as BpEntry | undefined;
  if (!cur || cur.position === undefined) return;
  ydoc.transact(() => {
    if (cur.hidden) yItems.set(key, { hidden: true });
    else yItems.delete(key);
  }, LOCAL_ORIGIN);
}

/** Whether ANY item in ANY grid of the doc carries an override at `bp` (a
 *  position pin or a hide) — drives the switcher's "this view has overrides" dot.
 *  Scans the doc's `grid:*` maps; pair with `ydoc.on("update", …)` for reactivity. */
export function hasOverridesForBreakpoint(
  ydoc: Y.Doc,
  bp: EditableBreakpoint,
): boolean {
  for (const [name, type] of ydoc.share) {
    if (!name.startsWith("grid:") || !(type instanceof Y.Map)) continue;
    let found = false;
    (type as Y.Map<unknown>).forEach((_v, k) => {
      const parsed = parseBpKey(k);
      if (parsed && parsed.bp === bp) found = true;
    });
    if (found) return true;
  }
  return false;
}

/** Mobile-tier convenience wrappers (the original single-tier API). */
export const readMobileFlags = (
  ydoc: Y.Doc,
  gridId: string,
  itemId: string,
): { overridden: boolean; hidden: boolean } =>
  readBreakpointFlags(ydoc, gridId, itemId, "mobile");
export const setMobileHidden = (
  ydoc: Y.Doc,
  gridId: string,
  itemId: string,
  hidden: boolean,
): void => setBreakpointHidden(ydoc, gridId, itemId, "mobile", hidden);

/** Subscribe to a grid's breakpoint-override changes (badge/hidden). Fires when
 *  any `::bp:` cell (or a legacy blob) is added, changed, or removed. */
export function observeBreakpoints(
  ydoc: Y.Doc,
  gridId: string,
  cb: () => void,
): () => void {
  const yItems = ydoc.getMap<unknown>(`grid:${gridId}`);
  const handler = (event: Y.YMapEvent<unknown>) => {
    for (const k of event.keysChanged) {
      if (k.startsWith(BP_PREFIX) || k === LEGACY_BP_KEY) {
        cb();
        return;
      }
    }
  };
  yItems.observe(handler);
  return () => yItems.unobserve(handler);
}

/**
 * The default MOBILE layout for items WITHOUT an explicit override (Phase 3
 * auto-stack): every item full-width (w = columns, x = 0), stacked top-to-bottom
 * in the desktop reading order (base y, then x), each keeping its height. So a
 * multi-column desktop section becomes a clean single column on mobile with zero
 * manual work — the responsive default. Pure function of the base layout, so it
 * re-derives (and reflows) whenever the desktop layout changes; never persisted.
 */
function autoStackLayout(
  bases: ReadonlyArray<{ id: string; position: GridItem["position"] }>,
  columns: number,
): PositionsByItem {
  const ordered = [...bases].sort(
    (a, b) => a.position.y - b.position.y || a.position.x - b.position.x,
  );
  const out: PositionsByItem = {};
  let y = 0;
  for (const it of ordered) {
    out[it.id] = { x: 0, y, w: columns, h: it.position.h };
    y += it.position.h;
  }
  return out;
}

/**
 * The layout an item occupies at the active breakpoint. Each non-desktop tier
 * resolves independently over the desktop base + its own sparse override; the
 * DEFAULT differs by tier:
 *   • mobile — auto-stack (full-width, stacked): a phone needs one column.
 *   • tablet — the desktop layout itself: a 768px tablet renders the 12-col grid
 *     fine, so tablet is opt-in fine-tuning, not a forced reflow.
 * (`autoStack` is only meaningful for mobile; pass `{}` otherwise.)
 */
function resolvePosition(
  item: GridItem,
  overrides: BreakpointOverrides,
  autoStack: PositionsByItem,
  bp: Breakpoint,
): GridItem["position"] {
  if (bp === "mobile") {
    return (
      overrides.mobile?.positions?.[item.id] ??
      autoStack[item.id] ??
      item.position
    );
  }
  if (bp === "tablet") {
    return overrides.tablet?.positions?.[item.id] ?? item.position;
  }
  return item.position;
}

/** A grid's per-breakpoint published layouts, resolved from the doc. */
export interface ResolvedGridLayouts {
  desktop: PositionsByItem;
  tablet: PositionsByItem;
  mobile: PositionsByItem;
  hidden: { tablet: Record<string, true>; mobile: Record<string, true> };
}

/**
 * Resolve a grid's published layouts DIRECTLY from the Yjs doc, independent of
 * the (breakpoint-tainted) store and the active breakpoint. The publish pipeline
 * calls this per grid: `desktop` is each item's base position, `tablet`/`mobile`
 * their resolved per-tier layouts (override → tier default → base), and `hidden`
 * the per-tier hide sets. Keyed by item id so the renderer can emit the `@media`
 * cascade.
 */
export function resolveGridLayouts(
  ydoc: Y.Doc,
  gridId: string,
  columns: number,
): ResolvedGridLayouts {
  const overrides = readOverrides(ydoc.getMap<unknown>(`grid:${gridId}`));
  const bases = gridItemsOf(ydoc, gridId).map((item) => ({
    id: item.id,
    position: item.position,
  }));
  const autoStack = autoStackLayout(bases, columns);
  const desktop: PositionsByItem = {};
  const tablet: PositionsByItem = {};
  const mobile: PositionsByItem = {};
  for (const { id, position } of bases) {
    const item = { id, position } as GridItem;
    desktop[id] = position;
    tablet[id] = resolvePosition(item, overrides, autoStack, "tablet");
    mobile[id] = resolvePosition(item, overrides, autoStack, "mobile");
  }
  return {
    desktop,
    tablet,
    mobile,
    hidden: {
      tablet: overrides.tablet?.hidden ?? {},
      mobile: overrides.mobile?.hidden ?? {},
    },
  };
}

/**
 * ONE store→Yjs coordinator per Y.Doc, shared by every grid bound to it.
 *
 * Direction 1 rides grid 0.9.3's commit events instead of per-grid zustand
 * subscriptions + a hand-rolled settle debounce. `subscribeGridsCommitted`
 * fires ONE event per committed store mutation naming every touched grid — a
 * CROSS-GRID move (a widget dragged between sections, a section between pages)
 * is a single event naming both grids. The coordinator runs the flushers of
 * every bound grid the event names inside ONE `transact(LOCAL_ORIGIN)`, so the
 * transfer commits together and undoes together (one gesture = one undo step;
 * an item id can never survive in both grids after an undo).
 *
 * Events are routed, not debounced:
 *  - `"remote"`   — the echo of our own inbound `applyRemoteLayout` (direction
 *    2). Skipped; this replaces the old `suppressLocalWrite` flag.
 *  - `"autogrow"` — async content growth (a widget re-measuring after a drop
 *    or while typing). Left to the settle pass below so a burst coalesces.
 *  - anything landing while a gesture is still live — left to the settle pass
 *    (transient mid-gesture states must never reach Yjs / the undo history).
 *  - everything else (gesture drops, programmatic CRUD/imports, store
 *    undo/redo) — flushed synchronously, one transact per event.
 *
 * The settle pass (`subscribeSettled`, default 200 ms quiesce) fires once per
 * gesture after the interaction has ended AND `"autogrow"`-tagged writes have
 * gone quiet; it reconciles EVERY bound grid in one transact. Reconciles diff
 * against what the Y.Map already holds, so untouched grids are free and a
 * net-zero burst (a post-release re-measure that snaps back) writes nothing.
 *
 * y-grid binds the SINGLETON `useGridStore` (a per-draft store would thread a
 * provider through the whole editor); its internal undo history therefore
 * stays on, silently accumulating alongside the authoritative Y.UndoManager.
 * If the editor ever moves to `createGridStore(…)`, pass `{ history: false }`
 * there so the CRDT history is the only one.
 */
interface DocSync {
  /** gridId → that binding's flush (reconcile items + config). */
  flushers: Map<string, () => void>;
  /** Unregister one binding; tears the coordinator down with the last one. */
  release: (gridId: string, flush: () => void) => void;
}
const docSyncs = new WeakMap<Y.Doc, DocSync>();
function docSyncOf(ydoc: Y.Doc): DocSync {
  const existing = docSyncs.get(ydoc);
  if (existing) return existing;
  const flushers = new Map<string, () => void>();
  const flushInOneTransact = (gridIds: Iterable<string>): void => {
    const toRun: Array<() => void> = [];
    for (const id of gridIds) {
      const flush = flushers.get(id);
      if (flush) toRun.push(flush);
    }
    if (toRun.length === 0) return;
    ydoc.transact(() => {
      for (const flush of toRun) flush();
    }, LOCAL_ORIGIN);
  };
  const unsubCommitted = subscribeGridsCommitted(useGridStore, (event) => {
    if (event.origin === "remote") return; // our own inbound echo
    if (event.origin === "autogrow") return; // coalesced by the settle pass
    if (selectIsInteracting(useGridStore.getState())) return; // mid-gesture
    flushInOneTransact(event.gridIds);
  });
  const unsubSettled = subscribeSettled(useGridStore, () => {
    flushInOneTransact([...flushers.keys()]);
  });
  const sync: DocSync = {
    flushers,
    release(gridId, flush) {
      if (flushers.get(gridId) === flush) flushers.delete(gridId);
      if (flushers.size === 0) {
        unsubCommitted();
        unsubSettled();
        docSyncs.delete(ydoc);
      }
    },
  };
  docSyncs.set(ydoc, sync);
  return sync;
}

type SyncedConfig = { gap: number; gapX?: number; gapY?: number };

function syncedConfigOf(config: {
  gap: number;
  gapX?: number;
  gapY?: number;
}): SyncedConfig {
  return {
    gap: config.gap,
    ...(config.gapX !== undefined ? { gapX: config.gapX } : {}),
    ...(config.gapY !== undefined ? { gapY: config.gapY } : {}),
  };
}

export interface GridBindingHandle {
  /** Tear down all subscriptions. Safe to call multiple times. */
  destroy(): void;
}

/**
 * Bind one grid's items to a shared Yjs document.
 *
 * @param ydoc    Shared Y.Doc (same instance or provider-synced).
 * @param gridId  The grid to bind. Must exist in `useGridStore` before
 *                or shortly after this is called; if the grid doesn't
 *                exist yet the binding waits for the first store write.
 * @returns       Handle with `destroy()` for cleanup.
 */
/**
 * Remove every item belonging to `gridId` from the flat store (`::parent` ===
 * gridId), plus any legacy items still inside the grid map. Pairs with clearing
 * the `grid:<id>` map (config/`::bp:`) when a page/section grid is torn down, so
 * a deleted page leaves no orphaned item bodies in the shared store.
 */
export function deleteGridItems(ydoc: Y.Doc, gridId: string): void {
  const yStore = ydoc.getMap<unknown>(ITEMS_MAP);
  const toDelete: string[] = [];
  yStore.forEach((v, id) => {
    if (parentOf(v) === gridId) toDelete.push(id);
  });
  if (toDelete.length === 0) return;
  ydoc.transact(() => {
    for (const id of toDelete) yStore.delete(id);
  }, LOCAL_ORIGIN);
}

export function bindYjsToGrid(ydoc: Y.Doc, gridId: string): GridBindingHandle {
  // Items live in the flat doc-level store (derived membership via `::parent`);
  // the per-grid map now holds only `::config` + `::bp:` cells.
  const yStore = ydoc.getMap<unknown>(ITEMS_MAP);
  const yMeta = ydoc.getMap<unknown>(`grid:${gridId}`);
  let destroyed = false;

  // Auto-stack over the CURRENT base (desktop) positions in Yjs. Recomputed on
  // demand so a desktop layout change reflows the mobile default automatically.
  const computeAutoStack = (): PositionsByItem => {
    const columns = useGridStore.getState().grids[gridId]?.config.columns ?? 12;
    const bases = gridItemsOf(ydoc, gridId).map((item) => ({
      id: item.id,
      position: item.position,
    }));
    return autoStackLayout(bases, columns);
  };

  // ── Direction 1: store → Yjs ──────────────────────────────────────
  type Items = Record<string, GridItem>;

  // Reconcile the grid's CURRENT store items into the Y.Map, diffing against
  // what the Y.Map ALREADY holds (NOT the previous store snapshot). Diffing
  // against Yjs is what makes deferring writes during a gesture safe: a
  // drag/resize streams many transient item states (a live auto-grow bumps a
  // widget's height, the pinned rect floats) before the final one. If we
  // buffered those against `prevItems` we'd still emit a net-zero A→B→A pair on
  // release — two real writes that cancel, but which leave a phantom step in the
  // UndoManager. Comparing the FINAL store state against Yjs instead: a gesture
  // that ends where it started (e.g. a resize that never crosses a cell
  // boundary) finds nothing to write, so no undo step is recorded.
  // Runs inside the doc coordinator's `transact(LOCAL_ORIGIN)`; the inner
  // transact below is nested (Yjs keeps the outer transaction + origin).
  const reconcileItems = () => {
    if (destroyed) return;
    const items = (useGridStore.getState().grids[gridId]?.items ?? {}) as Items;
    // The store holds the EFFECTIVE layout for the active breakpoint. On
    // desktop that IS the base; on mobile a moved item's store position is its
    // mobile override — route it to the override layer and KEEP the base
    // position (read from Yjs) so desktop is untouched. Content fields (props,
    // contentType, nesting) always write to the base item (shared across
    // breakpoints — only layout forks).
    const bp = activeBreakpoint;
    ydoc.transact(() => {
      // Removals from THIS grid's store. An item that left because it was
      // DELETED must be removed from the flat store; one that left because it
      // MOVED to another grid must NOT — the target grid's flush re-points its
      // `::parent`. We can't tell them apart from this grid alone, so consult the
      // store's global truth: delete only when the id is absent from EVERY grid.
      // (Cross-grid moves flush both grids in the same settle transaction, so
      // this is order-independent — whoever runs, the item is still in the
      // store under its new grid, so neither deletes it.)
      const gridsNow = useGridStore.getState().grids;
      const existsSomewhere = (id: string): boolean => {
        for (const g in gridsNow) if (gridsNow[g]?.items[id]) return true;
        return false;
      };
      yStore.forEach((v, id) => {
        if (parentOf(v) === gridId && !(id in items) && !existsSomewhere(id))
          yStore.delete(id);
      });
      if (bp === "desktop") {
        // Desktop: base writes. `writeItem` upserts the item's nested Y.Map in
        // the flat store (and re-points `::parent` if this is where it now
        // lives), so a height-only auto-grow touches just `position` inside the
        // map — never a top-level entry — and a move is a lone `::parent` write.
        for (const [id, item] of Object.entries(items)) {
          writeItem(yStore, id, item, gridId);
        }
        return;
      }
      // A non-desktop tier: split each store item into base (content, keep base
      // position) + a SPARSE per-item override cell `::bp:<bp>:<id>` — its
      // `position` set only when the item's position differs from what it would
      // INHERIT by default at this tier (mobile → its auto-stack slot; tablet →
      // the base). So an item left on the default keeps no position override
      // (still tracks the desktop layout); only a hand-placed one is pinned. The
      // cell's `hidden` flag is preserved.
      const autoStack = bp === "mobile" ? computeAutoStack() : {};
      for (const [id, item] of Object.entries(items)) {
        const prevVal = yStore.get(id);
        const prev =
          prevVal !== undefined ? readStoredItem(prevVal) : undefined;
        const base = prev?.position ?? item.position;
        const baseItem: GridItem = { ...item, position: base };
        writeItem(yStore, id, baseItem, gridId);
        const key = bpEntryKey(bp, id);
        const cell = yMeta.get(key) as BpEntry | undefined;
        const inherited = autoStack[id] ?? base;
        if (valueEqual(item.position, inherited)) {
          // Back on the tier default → drop the position override (keep hidden if
          // set; remove the whole cell if it held only a position).
          if (cell?.position !== undefined) {
            const next: BpEntry = { ...cell };
            delete next.position;
            if (!next.hidden) yMeta.delete(key);
            else yMeta.set(key, next);
          }
        } else if (!valueEqual(cell?.position, item.position)) {
          yMeta.set(key, { ...cell, position: item.position });
        }
      }
      // Sweep override cells whose item no longer belongs to THIS grid (deleted,
      // or moved away — a moved item's overrides don't follow it).
      yMeta.forEach((_v, k) => {
        const parsed = parseBpKey(k);
        if (parsed && !(parsed.itemId in items)) yMeta.delete(k);
      });
    }, LOCAL_ORIGIN);
  };

  // Store → Yjs, config subset. Same net-zero guard as items: a live gap drag
  // (grab, nudge, release on the same value) must not leave a phantom step.
  const reconcileConfig = () => {
    if (destroyed) return;
    const config = useGridStore.getState().grids[gridId]?.config;
    if (!config) return;
    const next = syncedConfigOf(config);
    if (valueEqual(yMeta.get(CONFIG_KEY), next)) return;
    ydoc.transact(() => {
      yMeta.set(CONFIG_KEY, next);
    }, LOCAL_ORIGIN);
  };

  // This grid's flush: reconcile items + config against the CURRENT store
  // state. Registered with the per-doc coordinator, which invokes it — inside
  // ONE shared `transact(LOCAL_ORIGIN)` alongside every other bound grid the
  // commit event names — on each committed store mutation, and again for every
  // bound grid when the gesture/auto-grow settle pass fires (see docSyncOf).
  const flushGrid = () => {
    if (destroyed) return;
    reconcileItems();
    reconcileConfig();
  };
  const docSync = docSyncOf(ydoc);
  docSync.flushers.set(gridId, flushGrid);

  // ── Direction 2: Yjs → store (+ breakpoint re-seed) ────────────────
  // Re-derive this grid's effective layout from the CURRENT doc state,
  // resolving each item's position for the ACTIVE breakpoint (its mobile
  // override, or the inherited base). Returns undefined while the store grid
  // doesn't exist yet (grid lifecycle is editor-owned — the binding waits).
  // This is also the `read` callback of applyRemoteLayout's invalidate-and-
  // reread contract: the store calls it at gesture end to re-read the merged
  // doc snapshot instead of replaying a stale queued payload.
  const readLayoutFromDoc = (): SerializedGridLayout | undefined => {
    const grid = useGridStore.getState().grids[gridId];
    if (!grid) return undefined;
    const overrides = readOverrides(yMeta);
    const autoStack = activeBreakpoint === "mobile" ? computeAutoStack() : {};
    const items: SerializedGridLayout["items"] = gridItemsOf(ydoc, gridId).map(
      (item) => ({
        id: item.id,
        position: resolvePosition(item, overrides, autoStack, activeBreakpoint),
        constraints: item.constraints ?? {},
        contentType: item.contentType,
        contentProps: item.contentProps,
        // Preserve the nested-grid reference for section blocks; without it a
        // reloaded section publishes an empty grid (serializeSiteDoc reads
        // item.nestedGridId).
        ...(item.nestedGridId !== undefined
          ? { nestedGridId: item.nestedGridId }
          : {}),
      }),
    );
    // Omit `id` from config — SerializedGridLayout.config is Omit<GridConfig,'id'>
    const { id: _id, ...configWithoutId } = grid.config;
    void _id;
    const remoteConfig = yMeta.get(CONFIG_KEY) as SyncedConfig | undefined;
    // 2.0 stored-document migration: docs written by 0.8-era clients can
    // carry preventCollision/breakpoints in the synced config — the silent
    // alias shim is gone, so migrate EXPLICITLY (pure; reference-identical
    // on clean docs, so no-op re-seeds stay no-ops).
    return upgradeSerializedLayout({
      config: remoteConfig
        ? { ...configWithoutId, ...remoteConfig }
        : configWithoutId,
      items,
    });
  };

  // Shared by the initial seed, remote updates, AND a local breakpoint switch —
  // all three are "the Yjs doc is truth, re-derive the effective layout".
  // applyRemoteLayout diffs identity-preservingly (a no-op re-seed notifies
  // nobody), tags its commit `"remote"` (direction 1 skips the echo — no
  // suppressLocalWrite flag), and, while a gesture is live, defers by marking
  // the grid dirty and re-reading via `read` at gesture end.
  const seedFromYjs = () => {
    if (destroyed) return;
    const layout = readLayoutFromDoc();
    if (!layout) return;
    useGridStore.getState().applyRemoteLayout(gridId, layout, {
      read: () => readLayoutFromDoc(),
    });
  };

  // observeDEEP (not shallow) — items are nested Y.Maps in the SHARED flat store,
  // so a remote change is either a nested field write (e.g. another context's
  // auto-grow setting `position` inside an item map) or a `::parent` re-point (a
  // cross-grid move: the item leaves this grid or arrives in it). Either way this
  // grid re-derives its membership by filtering `::parent`. Watching the flat
  // store means EVERY grid re-seeds on ANY item change — correct (a move must
  // re-seed both the source and target grid) and cheap at our grid counts. yMeta
  // carries this grid's config + `::bp:` cells.
  const onRemote = (
    _events: Array<Y.YEvent<Y.AbstractType<unknown>>>,
    txn: Y.Transaction,
  ) => {
    if (txn.origin === LOCAL_ORIGIN || destroyed) return;
    seedFromYjs();
  };
  yStore.observeDeep(onRemote);
  yMeta.observeDeep(onRemote);

  // A breakpoint switch re-resolves this grid (mobile overrides ↔ base).
  reseeders.add(seedFromYjs);

  // ── One-time migration: pre-v2 `::breakpoints` blob → per-item `::bp:` cells.
  // The blob was a single whole-object value (last-write-wins across all mobile
  // edits); the per-item cells merge concurrently. Idempotent + convergent (two
  // peers migrating the same blob produce equivalent cells), and LOCAL_ORIGIN so
  // it doesn't bounce back through onRemote. Only fires on a doc that still holds
  // the legacy key; fresh/new-format docs no-op.
  const legacy = yMeta.get(LEGACY_BP_KEY) as BreakpointOverrides | undefined;
  if (legacy && typeof legacy === "object") {
    ydoc.transact(() => {
      for (const bp of ["mobile", "tablet"] as const) {
        const layer = legacy[bp];
        if (!layer) continue;
        const ids = new Set([
          ...Object.keys(layer.positions ?? {}),
          ...Object.keys(layer.hidden ?? {}),
        ]);
        for (const id of ids) {
          const entry: BpEntry = {};
          const pos = layer.positions?.[id];
          if (pos) entry.position = pos;
          if (layer.hidden?.[id]) entry.hidden = true;
          if (entry.position !== undefined || entry.hidden)
            yMeta.set(bpEntryKey(bp, id), entry);
        }
      }
      yMeta.delete(LEGACY_BP_KEY);
    }, LOCAL_ORIGIN);
  }

  // ── One-time migration: legacy items stored INSIDE the grid map → the flat
  // single-parent store. Pre-flat docs (≤ webapp 0.0.78) keep item bodies as
  // `grid:<id>` map entries; move each into `grid-items` tagged `::parent =
  // gridId`, then drop it from the grid map (leaving only `::config`/`::bp:`).
  // Idempotent + convergent: `has` guards a double-move, and two peers migrating
  // the same doc converge (same itemId keys). LOCAL_ORIGIN so it doesn't bounce
  // back through onRemote. Fresh/already-flat docs no-op.
  const legacyItemIds: string[] = [];
  yMeta.forEach((_v, k) => {
    if (!isReserved(k)) legacyItemIds.push(k);
  });
  if (legacyItemIds.length > 0) {
    ydoc.transact(() => {
      for (const id of legacyItemIds) {
        const v = yMeta.get(id);
        if (v !== undefined && !yStore.has(id))
          yStore.set(id, itemToYMap(readStoredItem(v), gridId));
        yMeta.delete(id);
      }
    }, SEED_ORIGIN);
  }

  // ── Initial sync ──────────────────────────────────────────────────
  if (gridItemsOf(ydoc, gridId).length > 0 || yMeta.size > 0) {
    // Y.Doc has data for this grid → populate the store, resolved for the active
    // breakpoint.
    seedFromYjs();
  } else {
    // Store has data → push to Y.Doc (session originator). Fresh grids have no
    // overrides yet, so store positions ARE the base; write them straight.
    const grid = useGridStore.getState().grids[gridId];
    const items = grid?.items ?? {};
    if (grid && Object.keys(items).length > 0) {
      // Non-undoable while the doc hydrates (fresh-site seed); undoable after,
      // so a mid-session add's items undo together with its block (no orphan).
      const origin = seedingDocs.has(ydoc) ? SEED_ORIGIN : LOCAL_ORIGIN;
      ydoc.transact(() => {
        for (const [id, item] of Object.entries(items)) {
          yStore.set(id, itemToYMap(item, gridId));
        }
        yMeta.set(CONFIG_KEY, syncedConfigOf(grid.config));
      }, origin);
    }
  }

  return {
    destroy() {
      if (destroyed) return;
      destroyed = true;
      docSync.release(gridId, flushGrid);
      reseeders.delete(seedFromYjs);
      yStore.unobserveDeep(onRemote);
      yMeta.unobserveDeep(onRemote);
    },
  };
}

// ── Helpers ───────────────────────────────────────────────────────

/**
 * Structural (deep) equality — KEPT as the one local shim after the 0.9.3
 * rewrite, scoped to the OUTBOUND write path only. Inbound echo/no-op guarding
 * moved into the store (`applyRemoteLayout`'s identity-preserving diff), but a
 * Y.Map entry is an LWW register: `set`-ting a fresh object of the SAME values
 * is a real, undoable write. Field-level value comparison in `updateItemMap`/
 * `reconcileConfig`/the breakpoint-override split is what turns a net-zero
 * reconcile into a no-write (e.g. a resize that doesn't cross a cell boundary
 * leaves no phantom undo step) — item `contentProps` are arbitrary JSON, so a
 * flat compare isn't enough. Correctness-first: any genuine change still
 * differs and writes.
 */
function valueEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (
    typeof a !== "object" ||
    typeof b !== "object" ||
    a === null ||
    b === null
  ) {
    return false;
  }
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    if (
      !valueEqual(
        (a as Record<string, unknown>)[k],
        (b as Record<string, unknown>)[k],
      )
    ) {
      return false;
    }
  }
  return true;
}

// ── Nested-map item storage ─────────────────────────────────────────
// Items are stored as nested Y.Maps (see the module header). These three
// helpers are the only place that shape is read/written; everything else works
// in plain GridItem objects. Legacy docs store items as plain objects — reads
// tolerate both; a write upgrades the entry to a nested map.

/** The optional GridItem fields; the rest are always present. */
const OPTIONAL_ITEM_FIELDS = ["contentProps", "nestedGridId"] as const;

/** Read a stored item value — a nested Y.Map (current) or a plain object
 *  (legacy) — into a plain GridItem. */
function readStoredItem(v: unknown): GridItem {
  if (!(v instanceof Y.Map)) return v as GridItem;
  const m = v as Y.Map<unknown>;
  const item: Record<string, unknown> = {
    id: m.get("id"),
    position: m.get("position"),
    constraints: m.get("constraints") ?? {},
    contentType: m.get("contentType"),
  };
  for (const f of OPTIONAL_ITEM_FIELDS) if (m.has(f)) item[f] = m.get(f);
  return item as unknown as GridItem;
}

/** The grid an item belongs to (its `::parent`), or undefined for a legacy
 *  plain-object entry that predates the flat store. */
function parentOf(v: unknown): string | undefined {
  if (!(v instanceof Y.Map)) return undefined;
  return (v as Y.Map<unknown>).get(PARENT_KEY) as string | undefined;
}

/** Build a fresh, detached nested Y.Map for a new item, tagged with its owning
 *  grid. Integrated by the caller's `yStore.set(id, …)`. */
function itemToYMap(item: GridItem, parentGridId: string): Y.Map<unknown> {
  const m = new Y.Map<unknown>();
  m.set(PARENT_KEY, parentGridId);
  m.set("id", item.id);
  m.set("position", item.position);
  m.set("constraints", item.constraints ?? {});
  m.set("contentType", item.contentType);
  const rec = item as unknown as Record<string, unknown>;
  for (const f of OPTIONAL_ITEM_FIELDS)
    if (rec[f] !== undefined) m.set(f, rec[f]);
  return m;
}

/** Update an existing item's nested Y.Map fields IN PLACE, diffing by value so
 *  unchanged fields aren't rewritten (no phantom undo steps) — and, crucially,
 *  so a height-only re-measure touches just `position` INSIDE the map, never the
 *  top-level entry. */
function updateItemMap(m: Y.Map<unknown>, item: GridItem): void {
  const setIf = (k: string, val: unknown) => {
    if (!valueEqual(m.get(k), val)) m.set(k, val);
  };
  setIf("id", item.id);
  setIf("position", item.position);
  setIf("constraints", item.constraints ?? {});
  setIf("contentType", item.contentType);
  const rec = item as unknown as Record<string, unknown>;
  for (const f of OPTIONAL_ITEM_FIELDS) {
    if (rec[f] !== undefined) setIf(f, rec[f]);
    else if (m.has(f)) m.delete(f);
  }
}

/** Upsert a store item into the flat store at `id`, owned by `parentGridId`:
 *  update the nested map's fields in place when one already lives there (and
 *  re-point `::parent` if it changed — that single write IS a cross-grid move),
 *  else create a fresh nested map (also upgrades a legacy plain-object entry).
 *  Callers run inside a `transact(LOCAL_ORIGIN)`. */
function writeItem(
  yStore: Y.Map<unknown>,
  id: string,
  item: GridItem,
  parentGridId: string,
): void {
  const prev = yStore.get(id);
  if (prev instanceof Y.Map) {
    updateItemMap(prev, item);
    if (prev.get(PARENT_KEY) !== parentGridId)
      prev.set(PARENT_KEY, parentGridId);
  } else {
    yStore.set(id, itemToYMap(item, parentGridId));
  }
}

/** Enumerate a grid's items as plain GridItems: entries in the flat store whose
 *  `::parent` is this grid, PLUS any LEGACY items still living inside the grid's
 *  own map (a pre-migration doc read without an active binding — publish/tests).
 *  An item is only ever in ONE of the two places (migration moves it atomically),
 *  so there's no double-counting. */
function gridItemsOf(ydoc: Y.Doc, gridId: string): GridItem[] {
  const out: GridItem[] = [];
  ydoc.getMap<unknown>(ITEMS_MAP).forEach((v) => {
    if (parentOf(v) === gridId) out.push(readStoredItem(v));
  });
  ydoc.getMap<unknown>(`grid:${gridId}`).forEach((v, k) => {
    if (!isReserved(k)) out.push(readStoredItem(v));
  });
  return out;
}
